// Display · Alert
//
// Status notification with tone (success/info/warning/error-mapped), optional
// close button, optional declarative action buttons, and an optional full-width
// banner mode for top-of-page placement.
//
// DISTINCT FROM InfoBox:
//   - Alert has a semantic tone (any `ToneName` — green/red/amber/blue/
//     teal/purple/grey/orange/lime/pink/cyan/stone).
//   - Alert can be dismissed (closable).
//   - Alert can carry action buttons.
//   - Alert supports banner mode (full-width, no radius, flush to edges).
//   InfoBox is always neutral grey, always static, always contextual.
//
// Composite — auto-emits:
//   - `alert` (overridable via testId) on the root element.
//   - `alert-action-{id}` on each action button.
//   - `alert-close` on the close button.
//
// Ships a sibling `Alert.e2e.ts` with `AlertLocator`.
//
// All visual values from `@ds/tokens/palette`.

import type { ReactNode } from "react";
import { TONE, type ToneName } from "@ds/tokens/palette";
import { Box } from "@ds/layout/Box";
import { Inline } from "@ds/layout/Inline";
import { Stack } from "@ds/layout/Stack";
import { TextValue } from "@ds/display/TextValue";
import { Icon, type IconName } from "@ds/display/Icon";
import { Button } from "@ds/primitives/Button";

// ─── Icon mapping ─────────────────────────────────────────────────────────
//
// Default icon per tone. Consumers may override or suppress (icon={null}).

// NOTE: `info` (circle-i) is not yet in the IconName union. Until it ships,
// blue/teal/purple/grey fall back to `history` (clock circle) which is the most
// neutral "contextual information" icon available in the current set.
// When `info` lands in Icon.tsx, update the four entries below and remove this note.
//
// Resolved mapping:
//   green  → checkCircle   (✓ circle — success)
//   red    → xCircle       (✗ circle — error)
//   amber  → warning       (⚠ triangle — warning)
//   blue   → history       (clock circle — informational fallback)
//   teal   → history       (clock circle — informational fallback)
//   purple → history       (clock circle — informational fallback)
//   grey   → history       (clock circle — informational fallback)

const TONE_ICON: Record<ToneName, IconName> = {
  green:  "checkCircle",
  red:    "xCircle",
  amber:  "warning",
  blue:   "history",
  teal:   "history",
  purple: "history",
  grey:   "history",
  // Colour-coding tones — they carry no status meaning of their own, so they
  // take the same neutral icon the other non-status tones use.
  orange: "history",
  lime:   "history",
  pink:   "history",
  cyan:   "history",
  stone:  "history",
} as const;

// ─── Side-action shape ────────────────────────────────────────────────────

export interface AlertAction {
  /** Stable identifier — used for testid `alert-action-{id}`. */
  id: string;
  /** Button label. */
  label: ReactNode;
  /** Visual variant. Default `"default"`. */
  variant?: "primary" | "default" | "danger";
  /** Called on button click. May return a Promise (button shows loading). */
  onTrigger: () => void | Promise<void>;
  /** Show loading spinner on the button. */
  loading?: boolean;
  /** Disable the button. */
  disabled?: boolean;
}

// ─── Inputs (data props — the model) ─────────────────────────────────────

export interface AlertInput {
  /** Status tone. Drives background, border, text, and default icon. */
  tone: ToneName;
  /** Headline text — required. */
  title: ReactNode;
  /** Optional supporting paragraph rendered below the title. */
  description?: ReactNode;
  /**
   * Override the default tone-derived icon.
   * Pass `null` to suppress the icon entirely.
   * Omit (or `undefined`) to use the default tone icon.
   */
  icon?: ReactNode | null;
  /** Show × close button. */
  closable?: boolean;
  /**
   * Banner mode — full-width, no border-radius, flush layout.
   * Intended for top-of-page placement above the main content area.
   * Default: `false`.
   */
  banner?: boolean;
  /** `data-testid` on the root element. Defaults to `"alert"`. */
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ─────────────────────────

export interface AlertOutput {
  /** Fired when the user clicks the × close button. */
  onClose?: () => void;
}

// ─── Side actions (configurable affordances) ─────────────────────────────

export interface AlertActions {
  /** Optional action buttons rendered right-aligned beside the close button. */
  actions?: AlertAction[];
}

export type AlertProps = AlertInput & AlertOutput & AlertActions;

// ─── Implementation ───────────────────────────────────────────────────────

export function Alert({
  tone,
  title,
  description,
  icon,
  closable,
  banner = false,
  testId = "alert",
  onClose,
  actions,
}: AlertProps) {
  const t = TONE[tone];

  // Resolve icon node:
  //  - icon={null}      → suppress
  //  - icon={undefined} → use default tone icon
  //  - icon={<node>}    → use provided node
  const resolvedIcon: ReactNode =
    icon === null
      ? null
      : icon !== undefined
        ? icon
        : <Icon name={TONE_ICON[tone]} size="md" tone={tone} />;

  const hasActions = actions && actions.length > 0;
  const hasTopRight = closable || hasActions;

  return (
    <Box
      testId={testId}
      tone={tone}
      border
      shape={banner ? "rectangle" : "rounded-lg"}
      size="md"
    >
      <Inline gap="md" align="start" fullWidth>
        {/* Leading icon */}
        {resolvedIcon !== null && resolvedIcon}

        {/* Body: title + description */}
        <Stack gap="xs" fullWidth>
          <TextValue size="md" weight="semibold" lineHeight="normal" tone={tone}>
            {title}
          </TextValue>
          {description && (
            <TextValue size="sm" lineHeight="normal" tone="secondary">
              {description}
            </TextValue>
          )}
        </Stack>

        {/* Top-right: actions + close */}
        {hasTopRight && (
          <Inline gap="xs" align="center">
            {hasActions &&
              actions.map((action) => (
                <Button
                  key={action.id}
                  label={action.label}
                  variant={action.variant ?? "default"}
                  size="sm"
                  loading={action.loading}
                  disabled={action.disabled}
                  testId={`alert-action-${action.id}`}
                  onClick={() => { void action.onTrigger(); }}
                />
              ))}
            {closable && (
              <Button
                variant="text"
                size="sm"
                icon={<Icon name="close" size="sm" tone="current" />}
                label=""
                ariaLabel="Zamknij"
                testId="alert-close"
                onClick={onClose}
                style={{ color: t.text }}
              />
            )}
          </Inline>
        )}
      </Inline>
    </Box>
  );
}
